import { useShallow } from 'zustand/react/shallow';
import { Button } from '../../../components/ui/button';
import { useAnalytics } from '../../../core/analytics/useAnalytics';
import { useKeybindingsStore } from '../store/keybindingsStore';
import { canUndoNotice, durableNotices } from '../store/keybindingsSelectors';
import { KeybindingNoticeRow } from './KeybindingNoticeRow';
import { noticeText, type NoticeTextContext } from './keybindingDisplay';

/**
 * Top of the Settings tab: the durable changes the last validation made (a
 * displaced binding, a refused reserved chord) with one-click Undo, and the
 * overrides that belong to commands from another app version.
 */
export function KeybindingNoticeList({ textContext }: { readonly textContext: NoticeTextContext }) {
  const { track } = useAnalytics();
  const notices = useKeybindingsStore(useShallow(durableNotices));
  const undoSnapshot = useKeybindingsStore((state) => state.undoSnapshot);
  const unknownIds = useKeybindingsStore((state) => state.unknownOverrideIds);

  if (notices.length === 0 && unknownIds.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="keybinding-notices">
      {notices.map((notice, index) => (
        <KeybindingNoticeRow
          key={`${notice.kind}:${notice.commandId}:${notice.chord}:${index}`}
          notice={notice}
          text={noticeText(notice, textContext)}
          canUndo={canUndoNotice({ undoSnapshot }, notice)}
          onUndo={() => {
            useKeybindingsStore.getState().undoLastNotice();
            track('feature:keybinding_notice_undone');
          }}
        />
      ))}
      {unknownIds.length > 0 && (
        <div
          className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
          data-testid="keybindings-unknown-overrides"
        >
          <span className="flex-1">
            {unknownIds.length === 1 ? 'One shortcut is' : `${unknownIds.length} shortcuts are`} from another
            version of Jam SQL and do nothing here:{' '}
            <span className="font-mono">{unknownIds.join(', ')}</span>
          </span>
          <Button
            variant="ghost"
            size="xs"
            className="-my-1 h-6 px-2 text-xs"
            data-testid="keybindings-drop-unknown"
            onClick={() => {
              const count = unknownIds.length;
              useKeybindingsStore.getState().dropUnknownOverrides();
              track('feature:keybinding_unknown_dropped', { count });
            }}
          >
            Remove
          </Button>
        </div>
      )}
    </div>
  );
}
